/* eslint-disable no-nested-ternary */
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	Grid,
	Icon,
	IconButton,
	List,
	ListItem,
	ListItemText
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import FaiqActions from "redux-react/actions/faiqActions";
import { socket } from "redux-react/middleware/ws";

/* ------------------------------------------------------------------ *
 * Stages — human-facing wording, technical keys kept internal
 * ------------------------------------------------------------------ */
const STAGES = [
	{ key: "assets-sources", icon: "description", labelKey: "CONFIG.dataCheck.humanStep1" },
	{ key: "sources-knowledges", icon: "menu_book", labelKey: "CONFIG.dataCheck.humanStep2" },
	{ key: "vectors", icon: "search", labelKey: "CONFIG.dataCheck.humanStep3" }
];

const emptyStepState = () => ({
	status: "idle", // idle | loading | clean | anomalies | error
	report: null,
	vectorReport: null,
	patching: false,
	patchResult: null
});

const initialSteps = () => ({
	"assets-sources": emptyStepState(),
	"sources-knowledges": emptyStepState(),
	vectors: emptyStepState()
});

/* ------------------------------------------------------------------ *
 * Technical detail helpers (only shown inside the collapsed panel)
 * ------------------------------------------------------------------ */

const TotalTile = ({ label, value }) => (
	<Grid item xs={6} sm={4} md={3}>
		<MDBox
			px={1.5}
			py={1}
			borderRadius="md"
			sx={{ bgcolor: "grey.100", border: "1px solid", borderColor: "grey.200", height: "100%" }}
		>
			<MDTypography
				variant="caption"
				color="text"
				sx={{ textTransform: "uppercase", fontSize: 9.5, letterSpacing: 0.4, lineHeight: 1.2 }}
				display="block"
			>
				{label}
			</MDTypography>
			<MDTypography variant="h6" color="dark" fontWeight="bold" lineHeight={1.2}>
				{value ?? "-"}
			</MDTypography>
		</MDBox>
	</Grid>
);

const AnomalyItem = ({ assistantID, label, id, mapping, detailsType: detailsTypeProps }) => {
	const dispatch = useDispatch();
	const [expanded, setExpanded] = useState(false);
	const [loading, setLoading] = useState(false);
	const [detail, setDetail] = useState(null);
	const [loaded, setLoaded] = useState(false);

	const detailsType = mapping?.[id] ?? detailsTypeProps;

	const handleChange = (_, isExpanded) => {
		setExpanded(isExpanded);
		if (isExpanded && !loaded && detailsType && id) {
			setLoading(true);
			dispatch(
				FaiqActions.dataCheckDetails(
					assistantID,
					{ type: detailsType, ids: [id] },
					res => {
						const items = res?.data?.items ?? [];
						setDetail(items[0] ?? null);
						setLoading(false);
						setLoaded(true);
					},
					() => {
						setLoading(false);
						setLoaded(true);
					}
				)
			);
		}
	};

	return (
		<Accordion
			expanded={expanded}
			onChange={handleChange}
			disableGutters
			sx={{ bgcolor: "transparent", boxShadow: "none", "&:before": { display: "none" } }}
		>
			<AccordionSummary
				expandIcon={detailsType ? <ExpandMoreIcon /> : null}
				sx={{ minHeight: 30, "& .MuiAccordionSummary-content": { my: 0.5 } }}
			>
				<MDTypography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
					{label}
				</MDTypography>
			</AccordionSummary>
			{detailsType && (
				<AccordionDetails sx={{ bgcolor: "grey.100", borderRadius: 1 }}>
					{loading ? (
						<CircularProgress size={14} />
					) : detail ? (
						<MDBox
							component="pre"
							sx={{
								m: 0,
								fontSize: 11,
								whiteSpace: "pre-wrap",
								wordBreak: "break-word",
								maxHeight: 220,
								overflow: "auto"
							}}
						>
							{JSON.stringify(detail, null, 2)}
						</MDBox>
					) : (
						<MDTypography variant="caption">{t("CONFIG.dataCheck.notFound")}</MDTypography>
					)}
				</AccordionDetails>
			)}
		</Accordion>
	);
};

const AnomalySection = ({ assistantID, title, items, renderItem, getId, mapping, detailsType }) => {
	if (!items || items.length === 0) return null;
	return (
		<MDBox mb={1.5}>
			<MDTypography
				variant="caption"
				fontWeight="bold"
				color="text"
				sx={{ textTransform: "uppercase", fontSize: 10, letterSpacing: 0.4 }}
				display="block"
				mb={0.5}
			>
				{title} · {items.length}
			</MDTypography>
			<List
				dense
				disablePadding
				sx={{
					maxHeight: 200,
					overflow: "auto",
					bgcolor: "grey.100",
					borderRadius: 1,
					border: "1px solid",
					borderColor: "grey.200"
				}}
			>
				{items.map((item, index) => {
					const id = getId ? getId(item) : null;
					return (
						<ListItem key={index} divider sx={{ display: "block", py: 0 }}>
							{id ? (
								<AnomalyItem
									assistantID={assistantID}
									label={renderItem(item)}
									id={id}
									detailsType={detailsType}
									mapping={mapping}
								/>
							) : (
								<ListItemText
									primaryTypographyProps={{
										variant: "caption",
										sx: { fontFamily: "monospace", wordBreak: "break-all" }
									}}
									primary={renderItem(item)}
								/>
							)}
						</ListItem>
					);
				})}
			</List>
		</MDBox>
	);
};

/* ------------------------------------------------------------------ *
 * Custom stepper — built from plain MDBox so it is NOT affected by the
 * global Material-Dashboard MuiStepper/MuiStepIcon/MuiStepConnector theme
 * overrides (which force a gradient bar, shadow, etc.).
 * ------------------------------------------------------------------ */

/** A single status dot: number by default, spinner/check/! depending on status. */
const StepDot = ({ status, index, active }) => {
	let sx = {
		border: "2px solid",
		borderColor: "grey.300",
		color: "text.secondary",
		bgcolor: "transparent"
	};
	let content = (
		<MDTypography variant="caption" fontWeight="bold" color="inherit" lineHeight={1}>
			{index + 1}
		</MDTypography>
	);

	if (status === "loading") {
		sx = { bgcolor: "dark.main", color: "white" };
		content = <CircularProgress size={14} sx={{ color: "white" }} />;
	} else if (status === "clean") {
		sx = { bgcolor: "success.main", color: "white" };
		content = <Icon sx={{ fontSize: 18 }}>check</Icon>;
	} else if (status === "anomalies") {
		sx = { bgcolor: "warning.main", color: "white" };
		content = <Icon sx={{ fontSize: 18 }}>priority_high</Icon>;
	} else if (status === "error") {
		sx = { bgcolor: "error.main", color: "white" };
		content = <Icon sx={{ fontSize: 18 }}>close</Icon>;
	} else if (active) {
		sx = { bgcolor: "dark.main", color: "white" };
	}

	return (
		<MDBox
			display="flex"
			alignItems="center"
			justifyContent="center"
			sx={{ width: 34, height: 34, borderRadius: "50%", transition: "all .2s ease", ...sx }}
		>
			{content}
		</MDBox>
	);
};

/** The whole stepper header row (dots + labels + connectors). */
const CustomStepper = ({ stages, statuses, activeStep }) => (
	<MDBox display="flex" alignItems="flex-start" justifyContent="center" px={{ xs: 0, sm: 4 }}>
		{stages.map((stage, index) => (
			<Fragment key={stage.key}>
				{index > 0 && (
					<MDBox
						flex={1}
						mt="16px"
						sx={{
							height: "2px",
							borderRadius: 1,
							bgcolor: statuses[index - 1] === "clean" ? "success.main" : "grey.300",
							transition: "background-color .2s ease"
						}}
					/>
				)}
				<MDBox
					display="flex"
					flexDirection="column"
					alignItems="center"
					sx={{ width: 120, flexShrink: 0 }}
				>
					<StepDot status={statuses[index]} index={index} active={index === activeStep} />
					<MDTypography
						variant="button"
						fontWeight={index === activeStep ? "bold" : "regular"}
						color={index === activeStep ? "dark" : "text"}
						mt={0.75}
						textAlign="center"
					>
						{t(stage.labelKey)}
					</MDTypography>
				</MDBox>
			</Fragment>
		))}
	</MDBox>
);

/* ------------------------------------------------------------------ *
 * Main dialog
 * ------------------------------------------------------------------ */

const DataCheckDialog = ({ open, onClose, assistantID }) => {
	const dispatch = useDispatch();

	const [activeStep, setActiveStep] = useState(0);
	const [steps, setSteps] = useState(initialSteps());
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [showTech, setShowTech] = useState(false);

	const runCheckRef = useRef(() => {});

	const patchStepState = (key, patch) =>
		setSteps(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

	const isCleanMongo = (key, report) => {
		if (!report) return false;
		if (key === "assets-sources") {
			return (
				(report.sourcesWithoutAsset ?? []).length === 0 &&
				(report.duplicateSources ?? []).length === 0
			);
		}
		return (
			(report.knowledgeWithoutSource ?? []).length === 0 &&
			(report.duplicateKnowledges ?? []).length === 0
		);
	};

	const runCheck = key => {
		patchStepState(key, { status: "loading", report: null, vectorReport: null, patchResult: null });
		dispatch(
			FaiqActions.dataCheckStage(
				assistantID,
				key,
				res => {
					const report = res?.data ?? null;
					if (key === "vectors") {
						setSteps(prev => ({
							...prev,
							vectors: {
								...prev.vectors,
								report,
								status: prev.vectors.vectorReport ? prev.vectors.status : "loading"
							}
						}));
					} else {
						patchStepState(key, {
							report,
							status: isCleanMongo(key, report) ? "clean" : "anomalies"
						});
					}
				},
				() => patchStepState(key, { status: "error" })
			)
		);
	};
	runCheckRef.current = runCheck;

	const buildPatchBody = key => {
		const s = steps[key];
		if (key === "assets-sources") {
			return {
				sourcesWithoutAsset: s.report?.sourcesWithoutAsset ?? [],
				duplicateSources: s.report?.duplicateSources ?? []
			};
		}
		if (key === "sources-knowledges") {
			return {
				knowledgeWithoutSource: s.report?.knowledgeWithoutSource ?? [],
				duplicateKnowledges: s.report?.duplicateKnowledges ?? []
			};
		}
		return {
			missingInVectorDB: s.vectorReport?.missingInVectorDB ?? [],
			orphansInVectorDB: s.vectorReport?.orphansInVectorDB ?? [],
			mapping: s.report?.mapping ?? {}
		};
	};

	const runPatch = key => {
		setConfirmOpen(false);
		patchStepState(key, { patching: true, patchResult: null });
		dispatch(
			FaiqActions.dataCheckPatchStage(
				assistantID,
				key,
				buildPatchBody(key),
				res => {
					if (key === "vectors") {
						patchStepState(key, { patchMongoResult: res?.data ?? null });
					} else {
						patchStepState(key, { patching: false, patchResult: res?.data ?? null });
						runCheck(key);
					}
				},
				() => patchStepState(key, { patching: false, status: "error" })
			)
		);
	};

	/* websocket — vectors stage only */
	useEffect(() => {
		const onCheckResult = data => {
			if (data?.assistantID && assistantID && data.assistantID !== assistantID) return;
			if (data?.stage !== "vector") return;
			setSteps(prev => {
				const clean =
					(data.missingInVectorDB ?? []).length === 0 &&
					(data.orphansInVectorDB ?? []).length === 0;
				return {
					...prev,
					vectors: {
						...prev.vectors,
						vectorReport: data,
						status: data.success === false ? "error" : clean ? "clean" : "anomalies"
					}
				};
			});
		};
		const onPatchResult = data => {
			if (data?.assistantID && assistantID && data.assistantID !== assistantID) return;
			patchStepState("vectors", { patching: false, patchResult: data });
			runCheckRef.current("vectors");
		};
		socket.on("faiq_data_check_result", onCheckResult);
		socket.on("faiq_data_check_patch_result", onPatchResult);
		return () => {
			socket.off("faiq_data_check_result", onCheckResult);
			socket.off("faiq_data_check_patch_result", onPatchResult);
		};
	}, [assistantID]);

	useEffect(() => {
		if (open) {
			setActiveStep(0);
			setShowTech(false);
			setSteps(initialSteps());
			runCheck("assets-sources");
		}
	}, [open]);

	const handleClose = () => {
		setActiveStep(0);
		setShowTech(false);
		setSteps(initialSteps());
		onClose();
	};

	const goToStep = index => {
		setShowTech(false);
		setActiveStep(index);
		runCheck(STAGES[index].key);
	};

	/* derived */
	const currentKey = STAGES[activeStep].key;
	const current = steps[currentKey];
	const statusByIndex = STAGES.map(s => steps[s.key].status);

	const isVectors = currentKey === "vectors";
	const isLastStep = activeStep === STAGES.length - 1;
	const canProceed = current.status === "clean";
	const allClean = STAGES.every(s => steps[s.key].status === "clean");

	const issueCount = (() => {
		if (currentKey === "assets-sources") {
			return (
				(current.report?.sourcesWithoutAsset?.length ?? 0) +
				(current.report?.totals?.duplicateSources ?? 0)
			);
		}
		if (currentKey === "sources-knowledges") {
			return (
				(current.report?.knowledgeWithoutSource?.length ?? 0) +
				(current.report?.totals?.duplicateKnowledges ?? 0)
			);
		}
		return (
			(current.vectorReport?.missingInVectorDB?.length ?? 0) +
			(current.vectorReport?.orphansInVectorDB?.length ?? 0)
		);
	})();

	const cleanText = t(
		currentKey === "assets-sources"
			? "CONFIG.dataCheck.step1CleanText"
			: currentKey === "sources-knowledges"
				? "CONFIG.dataCheck.step2CleanText"
				: "CONFIG.dataCheck.step3CleanText"
	);
	const issueText = t(
		currentKey === "assets-sources"
			? "CONFIG.dataCheck.step1IssueText"
			: currentKey === "sources-knowledges"
				? "CONFIG.dataCheck.step2IssueText"
				: "CONFIG.dataCheck.step3IssueText"
	);

	/* ------- hero (main, non-technical) ------- */
	const renderHero = () => {
		if (current.status === "loading") {
			return (
				<MDBox display="flex" flexDirection="column" alignItems="center" py={5}>
					<CircularProgress sx={{ color: "dark.main" }} />
					<MDTypography variant="body2" color="text" mt={2}>
						{t("CONFIG.dataCheck.analyzing")}
					</MDTypography>
				</MDBox>
			);
		}

		if (current.status === "error") {
			const vectorModelNotFound =
				isVectors && current.vectorReport?.error === "vectorModelNotFound";
			return (
				<MDBox display="flex" flexDirection="column" alignItems="center" py={5}>
					<Icon sx={{ fontSize: 48, color: "error.main", mb: 1 }}>error_outline</Icon>
					<MDTypography variant="body2" color="text">
						{vectorModelNotFound
							? t("CONFIG.dataCheck.vectorModelNotFound")
							: t("CONFIG.dataCheck.error")}
					</MDTypography>
				</MDBox>
			);
		}

		if (current.patching) {
			return (
				<MDBox display="flex" flexDirection="column" alignItems="center" py={5}>
					<CircularProgress sx={{ color: "dark.main" }} />
					<MDTypography variant="body2" color="text" mt={2}>
						{t("CONFIG.dataCheck.correcting")}
					</MDTypography>
				</MDBox>
			);
		}

		if (current.status === "clean") {
			return (
				<MDBox display="flex" flexDirection="column" alignItems="center" py={5} px={3}>
					<Icon sx={{ fontSize: 52, color: "success.main", mb: 1.5 }}>check_circle</Icon>
					<MDTypography variant="h6" fontWeight="bold" color="dark">
						{t("CONFIG.dataCheck.stepCleanTitle")}
					</MDTypography>
					<MDTypography variant="body2" color="text" textAlign="center" mt={0.5}>
						{cleanText}
					</MDTypography>
				</MDBox>
			);
		}

		// anomalies
		return (
			<MDBox display="flex" flexDirection="column" alignItems="center" py={4} px={3}>
				<MDBox
					display="flex"
					alignItems="center"
					justifyContent="center"
					mb={2}
					sx={{
						width: 72,
						height: 72,
						borderRadius: "50%",
						bgcolor: "grey.100",
						border: "2px solid",
						borderColor: "warning.main"
					}}
				>
					<MDTypography variant="h4" fontWeight="bold" color="dark">
						{issueCount}
					</MDTypography>
				</MDBox>
				<MDTypography variant="body2" color="text" textAlign="center" sx={{ maxWidth: 420 }}>
					{issueText}
				</MDTypography>
				<MDButton
					variant="gradient"
					color="dark"
					onClick={() => setConfirmOpen(true)}
					sx={{ mt: 2.5 }}
				>
					<Icon sx={{ mr: 0.75 }}>{isVectors ? "sync" : "cleaning_services"}</Icon>
					{isVectors ? t("CONFIG.dataCheck.syncAction") : t("CONFIG.dataCheck.cleanAction")}
				</MDButton>
			</MDBox>
		);
	};

	/* ------- technical details (collapsed) ------- */
	const renderTechnical = () => {
		const totals = current.report?.totals ?? {};
		const vr = current.vectorReport ?? {};
		return (
			<MDBox
				mt={1}
				p={2}
				borderRadius="lg"
				sx={{ bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200" }}
			>
				<Grid container spacing={1} mb={2}>
					{currentKey === "assets-sources" && (
						<>
							<TotalTile label={t("CONFIG.dataCheck.assets")} value={totals.assets} />
							<TotalTile label={t("CONFIG.dataCheck.sources")} value={totals.sources} />
							<TotalTile
								label={t("CONFIG.dataCheck.sourcesWithoutAsset")}
								value={totals.sourcesWithoutAsset}
							/>
							<TotalTile
								label={t("CONFIG.dataCheck.duplicateSources")}
								value={totals.duplicateSources}
							/>
						</>
					)}
					{currentKey === "sources-knowledges" && (
						<>
							<TotalTile label={t("CONFIG.dataCheck.sources")} value={totals.sources} />
							<TotalTile label={t("CONFIG.dataCheck.knowledges")} value={totals.knowledges} />
							<TotalTile
								label={t("CONFIG.dataCheck.knowledgeWithoutSource")}
								value={totals.knowledgeWithoutSource}
							/>
							<TotalTile
								label={t("CONFIG.dataCheck.duplicateKnowledges")}
								value={totals.duplicateKnowledges}
							/>
						</>
					)}
					{isVectors && (
						<>
							<TotalTile
								label={t("CONFIG.dataCheck.expectedVectors")}
								value={totals.expectedVectors}
							/>
							<TotalTile label={t("CONFIG.dataCheck.totalVectorDB")} value={vr.totalVectorDB} />
							<TotalTile label={t("CONFIG.dataCheck.omittedVectors")} value={totals.omitVectors} />
						</>
					)}
				</Grid>

				{currentKey === "assets-sources" && (
					<>
						<AnomalySection
							assistantID={assistantID}
							title={t("CONFIG.dataCheck.sourcesWithoutAsset")}
							items={current.report?.sourcesWithoutAsset}
							renderItem={item => item?.code ?? JSON.stringify(item)}
							getId={item => item?.code}
							detailsType="source"
						/>
						<AnomalySection
							assistantID={assistantID}
							title={t("CONFIG.dataCheck.duplicateSources")}
							items={current.report?.duplicateSources}
							renderItem={item => `${item?.asset ?? ""} (×${item?.count ?? ""})`}
							getId={null}
						/>
					</>
				)}
				{currentKey === "sources-knowledges" && (
					<>
						<AnomalySection
							assistantID={assistantID}
							title={t("CONFIG.dataCheck.knowledgeWithoutSource")}
							items={current.report?.knowledgeWithoutSource}
							renderItem={item => `${item?.uuid ?? ""} (${item?.source ?? ""})`}
							getId={item => item?.uuid}
							detailsType="knowledge"
						/>
						<AnomalySection
							assistantID={assistantID}
							title={t("CONFIG.dataCheck.duplicateKnowledges")}
							items={current.report?.duplicateKnowledges}
							renderItem={item => item?.uuid ?? JSON.stringify(item)}
							getId={item => item?.uuid}
							detailsType="knowledge"
						/>
					</>
				)}
				{isVectors && (
					<>
						<AnomalySection
							assistantID={assistantID}
							title={t("CONFIG.dataCheck.missingInVectorDB")}
							items={current.vectorReport?.missingInVectorDB}
							renderItem={uuid => uuid}
							getId={uuid => uuid}
							mapping={current.report?.mapping ?? {}}
						/>
						<AnomalySection
							assistantID={assistantID}
							title={t("CONFIG.dataCheck.orphansInVectorDB")}
							items={current.vectorReport?.orphansInVectorDB}
							renderItem={uuid => uuid}
							getId={uuid => uuid}
							detailsType="vector"
						/>
					</>
				)}
			</MDBox>
		);
	};

	const hasReport = current.report || current.vectorReport;
	const canShowTech = hasReport && current.status !== "loading";

	return (
		<>
			<Dialog
				fullWidth
				maxWidth="md"
				open={open}
				onClose={handleClose}
				PaperProps={{ sx: { borderRadius: 3 } }}
			>
				{/* Header */}
				<MDBox display="flex" justifyContent="space-between" alignItems="center" px={3} py={2}>
					<MDBox display="flex" alignItems="center" gap={1.25}>
						<Icon sx={{ color: "dark.main" }}>verified_user</Icon>
						<MDTypography variant="h6" fontWeight="bold">
							{t("CONFIG.dataCheck.title")}
						</MDTypography>
					</MDBox>
					<MDBox display="flex" alignItems="center">
						<IconButton
							size="small"
							disabled={current.status === "loading" || current.patching}
							onClick={() => goToStep(activeStep)}
						>
							<Icon fontSize="small">refresh</Icon>
						</IconButton>
						<IconButton size="small" onClick={handleClose}>
							<Icon fontSize="small">close</Icon>
						</IconButton>
					</MDBox>
				</MDBox>

				<DialogContent sx={{ pt: 1 }}>
					{/* Stepper (custom — bypasses the global MD stepper theme overrides) */}
					<CustomStepper stages={STAGES} statuses={statusByIndex} activeStep={activeStep} />

					{/* Hero */}
					<MDBox mt={2}>{renderHero()}</MDBox>

					{/* Technical details (hidden by default) */}
					{canShowTech && (
						<MDBox mt={1} display="flex" flexDirection="column" alignItems="center">
							<MDButton
								variant="text"
								color="secondary"
								size="small"
								onClick={() => setShowTech(v => !v)}
							>
								{t("CONFIG.dataCheck.technicalDetails")}
								<Icon sx={{ ml: 0.5 }}>{showTech ? "expand_less" : "expand_more"}</Icon>
							</MDButton>
							<Collapse in={showTech} sx={{ width: "100%" }}>
								{renderTechnical()}
							</Collapse>
						</MDBox>
					)}
				</DialogContent>

				{/* Footer navigation */}
				<MDBox display="flex" alignItems="center" px={3} py={2}>
					<MDButton
						variant="text"
						color="secondary"
						disabled={activeStep === 0}
						onClick={() => goToStep(activeStep - 1)}
					>
						<Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
						{t("CONFIG.dataCheck.back")}
					</MDButton>

					<MDBox flex={1} display="flex" justifyContent="center" gap={0.75}>
						{STAGES.map((s, i) => (
							<MDBox
								key={s.key}
								sx={{
									width: i === activeStep ? 18 : 6,
									height: 6,
									borderRadius: 3,
									bgcolor: i === activeStep ? "dark.main" : "grey.300",
									transition: "all .2s ease"
								}}
							/>
						))}
					</MDBox>

					{isLastStep ? (
						<MDButton variant="gradient" color="dark" onClick={handleClose} disabled={!canProceed}>
							<Icon sx={{ mr: 0.5 }}>{allClean ? "check" : "done"}</Icon>
							{t("CONFIG.dataCheck.finish")}
						</MDButton>
					) : (
						<MDButton
							variant="gradient"
							color="dark"
							onClick={() => goToStep(activeStep + 1)}
							disabled={!canProceed}
						>
							{t("CONFIG.dataCheck.next")}
							<Icon sx={{ ml: 0.5 }}>arrow_forward</Icon>
						</MDButton>
					)}
				</MDBox>
			</Dialog>

			{/* Confirmation — plain language */}
			<Dialog
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { borderRadius: 3 } }}
			>
				<MDBox px={3} pt={3} pb={1} textAlign="center">
					<Icon sx={{ fontSize: 40, color: "warning.main", mb: 1 }}>
						{isVectors ? "sync" : "delete_sweep"}
					</Icon>
					<MDTypography variant="h6" fontWeight="bold">
						{isVectors ? t("CONFIG.dataCheck.syncAction") : t("CONFIG.dataCheck.confirmCleanTitle")}
					</MDTypography>
					<MDTypography variant="body2" color="text" mt={0.5}>
						{isVectors
							? t("CONFIG.dataCheck.confirmSyncText")
							: t("CONFIG.dataCheck.confirmCleanText", { count: issueCount })}
					</MDTypography>
				</MDBox>
				<DialogActions sx={{ px: 3, pb: 2, justifyContent: "center" }}>
					<MDButton variant="text" color="secondary" onClick={() => setConfirmOpen(false)}>
						{t("CONFIG.dataCheck.cancel")}
					</MDButton>
					<MDButton variant="gradient" color="dark" onClick={() => runPatch(currentKey)}>
						{t("CONFIG.dataCheck.confirmAction")}
					</MDButton>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default DataCheckDialog;
